import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { guardianApi } from "../lib/guardian";
import type { GuardianRequestResponse } from "../types/guardianTypes";
import { hasRole } from "../utils/AuthUtils";
import { requiresGuardianForTeen } from "../utils/GuardianSetupUtils";
import { useAuth } from "./useAuth";

const combineGuardianRelationships = (incoming: GuardianRequestResponse[], outgoing: GuardianRequestResponse[]) => {
  const relationshipsByGuardianId = new Map<string, GuardianRequestResponse>();

  [...incoming, ...outgoing].forEach((relationship) => {
    const relationshipKey = relationship.guardianUserId ?? relationship.targetEmail ?? relationship.requestedByUserId ?? "pending-invite";
    const existing = relationshipsByGuardianId.get(relationshipKey);
    if (!existing || relationship.status === "ACTIVE") {
      relationshipsByGuardianId.set(relationshipKey, relationship);
    }
  });

  return Array.from(relationshipsByGuardianId.values());
};

const useTeenGuardianRequirement = () => {
  const { user } = useAuth();
  const isTeen = hasRole(user?.roles, "TEEN");
  const requiresGuardian = Boolean(isTeen && requiresGuardianForTeen(user?.dateOfBirth));
  const shouldCheckGuardians = Boolean(user && requiresGuardian);

  const guardianStatusQuery = useQuery({
    queryKey: ["teen-guardian-requirement", user?.id],
    enabled: shouldCheckGuardians,
    queryFn: async () => {
      const [incoming, outgoing] = await Promise.all([guardianApi.listTeenIncoming(), guardianApi.listTeenOutgoing()]);
      return combineGuardianRelationships(incoming, outgoing);
    },
    staleTime: 60_000,
  });

  const relationships = guardianStatusQuery.data ?? [];

  const status = useMemo(() => {
    const activeGuardianCount = relationships.filter((relationship) => relationship.status === "ACTIVE").length;
    const pendingGuardianCount = relationships.filter((relationship) => relationship.status === "PENDING").length;

    return {
      requiresGuardian,
      hasActiveGuardian: !requiresGuardian || activeGuardianCount > 0,
      requiresSetup: requiresGuardian && activeGuardianCount === 0,
      activeGuardianCount,
      pendingGuardianCount,
    };
  }, [relationships, requiresGuardian]);

  return {
    ...status,
    isLoading: guardianStatusQuery.isLoading,
    isFetching: guardianStatusQuery.isFetching,
  };
};

export default useTeenGuardianRequirement;
